/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-02-07 14:04:34
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { getShop } from 'src/helpers';
import { checkPremiumStore } from 'src/helpers/subscription-helper';
import { isFreemiumPlan } from 'src/helpers/subscription-v4-helper';
import { store } from 'src/stores/store';
import apiService from 'src/services/api-service';

const BEACON_ID = 'f4b4f6bb-0980-4a6f-af56-aa8200d37aee';
const BEACON_ID_SHOPIFY_PLUS = '839803c9-0352-4a88-9032-3722336e7c63';
const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

/**
 * Track page changed event for Beacon
 */
export function trackPageChanged(routePath, pageName, beaconDocs) {
  // Identify customer info
  try {
    const tenant = getShop();
    if (tenant) {
      window.Beacon('identify', {
        'active-store': tenant,
        'type-support': getTypeSupport()
      });
    }

    // Add page view event
    window.Beacon('event', {
      type: 'page-viewed',
      url: document.location.href,
      title: `${document.title} - ${pageName}`
    });

    // Suggest documents
    const docs = getBeaconDocs(routePath, beaconDocs);
    window.Beacon('suggest', docs);
  } catch (error) {}
}

/**
 * Init beacon based on plan name and subcription status
 *
 * @param  {object} tenantApp
 */
export const initBeacon = (tenantApp, routePath, beaconDocs) => {
  const { tenantId } = tenantApp;

  // Destroy old mapping
  // window.Beacon('destroy');

  try {
    if (checkPremiumStore(tenantApp)) {
      window.Beacon('init', BEACON_ID_SHOPIFY_PLUS);
      // Prefill tenantId in Contact Form
      window.Beacon('prefill', {
        fields: [{ id: 29568, value: tenantId }]
      });
    } else {
      window.Beacon('init', BEACON_ID);
      // Prefill tenantId in Contact Form
      window.Beacon('prefill', {
        fields: [{ id: 29567, value: tenantId }]
      });
    }

    // Create user identify
    window.Beacon('identify', { 'active-store': tenantId });

    // Suggest documents
    const docs = getBeaconDocs(routePath, beaconDocs);
    window.Beacon('suggest', docs);
  } catch (error) {}

  // Notify to slack when user sent beacon email
  window.Beacon('on', 'email-sent', function (params) {
    const request = apiService.buildRequestPayload('admin-v3.beacon.beacon-send-email-notify', {});
    fetch(request.endpoint, {
      headers: request.headers,
      method: request.method,
      body: JSON.stringify(params)
    });
  });
};

/**
 * Get type support based on shopify plan name
 */
const getTypeSupport = () => {
  if (!store) return 'undefined';

  const currentState = store.getState();

  const externalInfo = _.get(currentState, 'oauth.tenantApp.externalInfo', {});
  const shopifyPlan = _.get(externalInfo, 'plan_name', '');

  return shopifyPlan ? (shopifyPlan === 'shopify_plus' ? 'Plus' : 'Regular') : 'undefined';
};

/**
 * Check for support shopify plus
 *
 * Need to support as shopify plus if the store status:
 *  - Shopify plus plan and subscribe from 30 June 2020 forward
 *  - Standart plan, subscribed plan 15 and up and subscribe from 30 June 2020 forward
 *
 * @param  {object} tenantApp
 *
 * @return {boolean}           support shopify plus or not (true | false)
 */
// const checkShopifyPlus = tenantApp => {
//   const { selectedPlan = '', chargeInfoActive = {}, externalInfo = '' } = tenantApp;
//   const activeStatus = _.get(chargeInfoActive, 'status', '');
//   const billingOn = _.get(chargeInfoActive, 'activated_on', 0);
//   const planName = _.get(externalInfo, 'plan_name', '');

//   if (!selectedPlan) return false;

//   const billingOnTimestamp = new Date(billingOn).getTime();
//   const checkPointTimestamp = new Date('2020-06-30').getTime();
//   if (
//     activeStatus !== 'active' ||
//     (planName === 'shopify_plus' && billingOnTimestamp < checkPointTimestamp) ||
//     (planName !== 'shopify_plus' &&
//       (_.includes(lowPlan, selectedPlan.replace('_discount', '')) ||
//         billingOnTimestamp < checkPointTimestamp))
//   ) {
//     return false;
//   }

//   return true;
// };

/**
 * Get suggest documents
 */
export const getBeaconDocs = (routePath: string, beaconDocs: Array<Object>): Array<Object> => {
  let documents = [];

  try {
    // routePath: /filter-trees/new or /filter-trees/filterId
    const trueRoutePath = routePath ? routePath.substring(1) : '';
    const split = trueRoutePath.split('/');

    // Skip => Get default docs configured in Beacon
    if (split.length === 1 && split[0] === '') return [];

    for (let i = split.length; i > 0; i--) {
      if (documents.length > 0) break;

      // Get document for each path
      const sub = _.slice(split, 0, i);
      const path = sub.join('/');

      // Find documents based on route path
      const group = _.findLast(beaconDocs, group => {
        return _.includes(group.routePath, path);
      });

      documents = _.get(group, 'documents', []);
    }
  } catch (error) {
    console.log('Detect docs error', error);
  }

  return documents;
};

/**
 * open beacon chat
 */
export const showBeaconChat = () => {
  window.Beacon('navigate', '/ask/chat/');
  window.Beacon('open');
};

/**
 * Init Intercom chatbox
 * @param {} tenantApp
 */
export const initIntercom = tenantApp => {
  // Don't show intercom with freemium plan
  const isFreemium = isFreemiumPlan(tenantApp);
  if (isFreemium) return;

  const email = tenantApp.externalInfo?.email;
  const { userId, name, createdAt, userHash } = tenantApp.intercom || {};
  console.log(`initIntercom(${email}, ${INTERCOM_APP_ID}, ${JSON.stringify(tenantApp.intercom)})`);

  INTERCOM_APP_ID &&
    email &&
    window.Intercom('boot', {
      app_id: INTERCOM_APP_ID,
      user_id: userId,
      name,
      email,
      user_hash: userHash,
      created_at: createdAt
    });
};

/**
 * Show chatbox
 */
export const showChatbox = () => {
  window.Intercom('show');
};
